import { turnoSchema } from '@projetive-op/domain'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'

import {
  BarraTitulo,
  BotaoEditar,
  Formulario,
  RodapeGaveta,
  aoSubmeter,
  useGavetaCadastro,
} from '@/components/cadastros/base'
import { Campo, Checkbox, Input } from '@/components/ui/campo'
import { Gaveta } from '@/components/ui/gaveta'
import { Ativo, Cabecalho, Carregando, Linha, Tabela, Td, Th, Vazio } from '@/components/ui/tabela'
import { useTRPC } from '@/lib/trpc'

export const Route = createFileRoute('/gestao/turnos')({ component: Turnos })

const DIAS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'] as const

interface Item {
  id: string
  nome: string
  inicio: string
  fim: string
  fimSexta: string | null
  intervaloInicio: string | null
  intervaloFim: string | null
  diasSemana: number[]
  ativo: boolean
}

function Turnos() {
  const trpc = useTRPC()
  const qc = useQueryClient()
  const lista = useQuery(trpc.cadastros.turnos.listar.queryOptions())
  const g = useGavetaCadastro<Item>()
  const invalidar = () =>
    qc.invalidateQueries({ queryKey: trpc.cadastros.turnos.listar.queryKey() })
  const criar = useMutation(
    trpc.cadastros.turnos.criar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )
  const atualizar = useMutation(
    trpc.cadastros.turnos.atualizar.mutationOptions({
      onSuccess: () => {
        void invalidar()
        g.fechar()
      },
    }),
  )

  const submit = aoSubmeter({
    schema: turnoSchema,
    numericosLista: ['diasSemana'],
    opcionais: ['fimSexta', 'intervaloInicio', 'intervaloFim'],
    booleanos: ['ativo'],
    setErros: g.setErros,
    sucesso: (d) => `Turno ${d.nome} salvo.`,
    onValido: (dados) =>
      g.item ? atualizar.mutateAsync({ id: g.item.id, ...dados }) : criar.mutateAsync(dados),
  })

  return (
    <>
      <BarraTitulo
        titulo="Turnos"
        descricao="Jornada de cada grupo: base do tempo disponível na produtividade. O intervalo de refeição fica fora dela: o almoço não pesa contra ninguém."
        onNovo={g.abrirNovo}
        rotuloNovo="Novo turno"
      />
      <Tabela>
        <Cabecalho>
          <Th>Nome</Th>
          <Th>Horário</Th>
          <Th>Intervalo</Th>
          <Th>Dias</Th>
          <Th>Pessoas</Th>
          <Th />
        </Cabecalho>
        <tbody>
          {lista.data?.map((t) => (
            <Linha key={t.id}>
              <Td className="font-medium">
                {t.nome} {!t.ativo && <Ativo ativo={false} />}
              </Td>
              <Td numerico>
                {t.inicio}–{t.fim}
                {t.fimSexta ? ` (sex. ${t.fimSexta})` : ''}
              </Td>
              <Td numerico className="text-fg-muted">
                {t.intervaloInicio && t.intervaloFim
                  ? `${t.intervaloInicio}–${t.intervaloFim}`
                  : '—'}
              </Td>
              <Td className="text-fg-muted">{t.diasSemana.map((d) => DIAS[d]).join(', ')}</Td>
              <Td numerico>{t._count.usuarios}</Td>
              <Td className="text-right">
                <BotaoEditar rotulo={t.nome} onClick={() => g.abrirEditar(t)} />
              </Td>
            </Linha>
          ))}
          {lista.isPending && <Carregando colunas={6} />}
          {lista.data?.length === 0 && <Vazio colunas={6}>Nenhum turno.</Vazio>}
        </tbody>
      </Tabela>
      <Gaveta
        aberta={g.aberta}
        titulo={g.item ? 'Editar turno' : 'Novo turno'}
        onFechar={g.fechar}
        rodape={
          <RodapeGaveta
            form="form-turno"
            salvando={criar.isPending || atualizar.isPending}
            onCancelar={g.fechar}
          />
        }
      >
        <Formulario id="form-turno" onSubmit={submit} erros={g.erros} onLimparErro={g.limparErro}>
          <Campo label="Nome" erro={g.erros.nome}>
            {(p) => <Input {...p} name="nome" defaultValue={g.item?.nome} autoFocus />}
          </Campo>
          <div className="grid grid-cols-3 gap-3">
            <Campo label="Início" erro={g.erros.inicio}>
              {(p) => (
                <Input {...p} name="inicio" type="time" defaultValue={g.item?.inicio ?? '06:45'} />
              )}
            </Campo>
            <Campo label="Fim" erro={g.erros.fim}>
              {(p) => <Input {...p} name="fim" type="time" defaultValue={g.item?.fim ?? '17:00'} />}
            </Campo>
            <Campo label="Fim na sexta" erro={g.erros.fimSexta} dica="Opcional">
              {(p) => (
                <Input {...p} name="fimSexta" type="time" defaultValue={g.item?.fimSexta ?? ''} />
              )}
            </Campo>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Campo
              label="Intervalo: início"
              erro={g.erros.intervaloInicio}
              dica="Almoço/refeição. Opcional."
            >
              {(p) => (
                <Input
                  {...p}
                  name="intervaloInicio"
                  type="time"
                  defaultValue={g.item?.intervaloInicio ?? ''}
                />
              )}
            </Campo>
            <Campo label="Intervalo: fim" erro={g.erros.intervaloFim}>
              {(p) => (
                <Input
                  {...p}
                  name="intervaloFim"
                  type="time"
                  defaultValue={g.item?.intervaloFim ?? ''}
                />
              )}
            </Campo>
          </div>
          <fieldset>
            <legend className="mb-2 text-sm font-medium">Dias da semana</legend>
            {g.erros.diasSemana && (
              <p role="alert" className="mb-2 text-xs font-medium text-vermelho">
                {g.erros.diasSemana}
              </p>
            )}
            <div className="flex flex-wrap gap-2">
              {DIAS.map((d, i) => (
                <label
                  key={d}
                  className="flex h-10 cursor-pointer items-center gap-2 rounded-xl bg-elevated px-3 text-sm font-medium select-none has-checked:bg-brand/20 has-checked:text-brand"
                >
                  <input
                    type="checkbox"
                    name="diasSemana[]"
                    value={i}
                    className="accent-brand"
                    defaultChecked={g.item ? g.item.diasSemana.includes(i) : i >= 1 && i <= 5}
                  />
                  {d}
                </label>
              ))}
            </div>
          </fieldset>
          <Checkbox name="ativo" label="Ativo" defaultChecked={g.item?.ativo ?? true} />
        </Formulario>
      </Gaveta>
    </>
  )
}
